// CAMINHO: src/components/ChangePasswordModal.tsx

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ChangePasswordModal = ({ isOpen, onOpenChange }: ChangePasswordModalProps) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Erro", description: "As senhas não coincidem.", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "Erro", description: "A senha deve ter no mínimo 6 caracteres.", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setIsSaving(false);

    if (error) {
      toast({ title: "Erro ao alterar senha", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Sucesso!", description: "Sua senha foi alterada." });
      setNewPassword('');
      setConfirmPassword('');
      onOpenChange(false);
    }
  };

  const label = 'block text-sm font-medium text-slate-700';
  const input = 'w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30';
  const LOGO = '/imgs/logos allana/letra_A_logo.png';
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-md overflow-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
          <div className="relative flex items-center gap-4 text-left">
            <img src={LOGO} alt="Allana Sthéfany" className="h-12 w-12 shrink-0 select-none object-contain opacity-80" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Segurança</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">Alterar senha</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword" className={label}>Nova senha</Label>
            <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo de 6 caracteres" className={input} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className={label}>Confirmar nova senha</Label>
            <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={input} />
            {mismatch && <p className="text-xs text-red-600">As senhas não coincidem.</p>}
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
          <DialogClose asChild><Button variant="outline" className="rounded-xl">Cancelar</Button></DialogClose>
          <Button onClick={handlePasswordChange} disabled={isSaving} className="rounded-xl bg-[#c4ab83] px-6 font-semibold text-slate-900 hover:bg-[#b39a72]">
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar nova senha
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
